import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import PropTypes from 'prop-types';
import { openProfileDocument } from '../../../backend/transport';
import { useNavigate } from '../../../backend/navigation';
import { POPUP_TYPE, ROUTES, TITLES } from '../../../constants';
import { TitleBar } from '../../common';
import { getLocalStorageItem, setLocalStorageItem, popup } from '../../../utils';
import { getMarketplaceLabelGoods, printMarketplaceLabels } from '../../../service/marketplaceLabels';
import './MarketplaceLabels.css';

// Amazon/Walmart are listed so vendors see where they will appear; only
// enabled marketplaces have a backend adapter.
export const MARKETPLACES = [
  { id: 'tiktok', name: 'TikTok Shop', enabled: true, route: ROUTES.MARKETPLACE_LABELS_TIKTOK },
  { id: 'amazon', name: 'Amazon', enabled: false },
  { id: 'walmart', name: 'Walmart', enabled: false },
];
// Mirrors the backend print contract so vendors get immediate feedback.
export const LIMITS = { MAX_GOODS: 100, MAX_QTY: 500, MAX_TOTAL: 1000 };
export const LABEL_FORMATS = [
  { id: '2.25x1.25', name: '2.25 × 1.25 in' },
  { id: '2.5x1.25', name: '2.5 × 1.25 in' },
];
const LABEL_FORMAT_STORAGE_KEY = 'marketplace-label-format';
const DEFAULT_LABEL_FORMAT = LABEL_FORMATS[0].id;

// Distinct non-empty values of one SKU field; a goods item normally maps to one SKU.
const skuValues = (item, field) => [...new Set((item.skus || []).map((sku) => sku[field]).filter(Boolean))].join(', ');

// Odoo picks the symbology so the preview draws exactly what the printed label will.
const PREVIEW_FORMATS = { Code128: 'CODE128', EAN13: 'EAN13' };

function BarcodePreview({ code, symbology }) {
  const ref = useRef(null);
  const [drawable, setDrawable] = useState(true);
  const format = PREVIEW_FORMATS[symbology];
  useEffect(() => {
    if (!format) return;
    // `valid` makes JsBarcode report unencodable input instead of throwing.
    JsBarcode(ref.current, code, { format, displayValue: false, height: 40, width: 1.4, margin: 0, valid: setDrawable });
  }, [code, format]);
  // The code stays in the accessible name; vendors only need to read it when no preview can be drawn.
  if (!format || !drawable) return <span className="font-monospace">{code}</span>;
  return <svg ref={ref} className="marketplace-labels__barcode" role="img" aria-label={`Barcode ${code}`} />;
}

BarcodePreview.propTypes = {
  code: PropTypes.string.isRequired,
  symbology: PropTypes.string,
};

const matches = (item, term) => {
  if (!term) return true;
  const haystack = [
    item.name, item.reference_code, item.goods_id, item.barcode?.code,
    ...(item.skus || []).flatMap((sku) => [sku.seller_sku, sku.product_name, sku.color, sku.size]),
  ];
  return haystack.some((value) => value && String(value).toLowerCase().includes(term));
};

export function validateSelection(selection) {
  const labels = Object.entries(selection).map(([goods_id, qty]) => ({ goods_id, quantity: Number(qty) }));
  if (!labels.length) return { error: 'Select at least one item to print.' };
  if (labels.length > LIMITS.MAX_GOODS) return { error: `Select at most ${LIMITS.MAX_GOODS} items per print.` };
  if (labels.some(({ quantity }) => !Number.isInteger(quantity) || quantity < 1 || quantity > LIMITS.MAX_QTY)) {
    return { error: `Each quantity must be a whole number from 1 to ${LIMITS.MAX_QTY}.` };
  }
  const total = labels.reduce((sum, { quantity }) => sum + quantity, 0);
  if (total > LIMITS.MAX_TOTAL) return { error: `Print at most ${LIMITS.MAX_TOTAL} labels at once (selected ${total}).` };
  return { labels, total };
}

// Rendered without a marketplace at the tile entry, which forwards to the first one.
export const MarketplaceLabels = ({ marketplaceId = null }) => {
  const navigate = useNavigate();
  const marketplace = MARKETPLACES.find((item) => item.id === marketplaceId && item.enabled);

  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | loaded | error
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState({});
  const [printing, setPrinting] = useState(false);
  const [labelFormat, setLabelFormat] = useState(() => {
    const stored = getLocalStorageItem(LABEL_FORMAT_STORAGE_KEY);
    return LABEL_FORMATS.some(({ id }) => id === stored) ? stored : DEFAULT_LABEL_FORMAT;
  });
  const generation = useRef(0);

  useEffect(() => {
    if (!marketplaceId) navigate(MARKETPLACES[0].route, { replace: true });
  }, [marketplaceId, navigate]);

  const load = useCallback(async () => {
    if (!marketplace) return;
    const current = ++generation.current;
    setStatus('loading');
    setSelection({});
    try {
      const response = await getMarketplaceLabelGoods({ data: { marketplace: marketplace.id } });
      if (current !== generation.current) return;
      if (!response || !Array.isArray(response.data?.items)) {
        setItems([]);
        setStatus('error');
        return;
      }
      setItems(response.data.items);
      setStatus('loaded');
    } catch {
      if (current === generation.current) setStatus('error');
    }
  }, [marketplace]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  const term = search.trim().toLowerCase();
  const visible = useMemo(() => items.filter((item) => matches(item, term)), [items, term]);
  const printable = visible.filter((item) => item.barcode);
  const allVisibleSelected = printable.length > 0 && printable.every((item) => item.goods_id in selection);
  const selectedCount = Object.keys(selection).length;
  const selectedTotal = Object.values(selection).reduce((sum, qty) => sum + (Number(qty) || 0), 0);

  const toggle = (goodsId) => setSelection((previous) => {
    const next = { ...previous };
    if (goodsId in next) delete next[goodsId];
    else next[goodsId] = '1';
    return next;
  });
  const toggleAll = () => setSelection((previous) => {
    const next = { ...previous };
    printable.forEach((item) => {
      if (allVisibleSelected) delete next[item.goods_id];
      else if (!(item.goods_id in next)) next[item.goods_id] = '1';
    });
    return next;
  });
  const setQuantity = (goodsId, value) => setSelection((previous) => ({ ...previous, [goodsId]: value }));
  const chooseLabelFormat = (event) => {
    const format = event.target.value;
    setLabelFormat(format);
    setLocalStorageItem(LABEL_FORMAT_STORAGE_KEY, format);
  };

  const print = async () => {
    const { labels, error } = validateSelection(selection);
    if (error) {
      popup(POPUP_TYPE.ERR, error);
      return;
    }
    setPrinting(true);
    try {
      // The default size is the backend's default; omitting it keeps printing working on a
      // backend that predates label_format, so the two deploys need no fixed order.
      const format = labelFormat === DEFAULT_LABEL_FORMAT ? {} : { label_format: labelFormat };
      const response = await printMarketplaceLabels({ data: { marketplace: marketplace.id, labels, ...format } });
      if (!response) return; // fetchData already reported the error
      const documentUrl = Array.isArray(response.data) ? response.data[0] : null;
      if (!documentUrl) {
        popup(POPUP_TYPE.ERR, response.message || 'The label PDF was not returned.');
        return;
      }
      openProfileDocument(documentUrl, '_blank');
    } catch (failure) {
      popup(POPUP_TYPE.ERR, failure.message || 'Could not open the label PDF.');
    } finally {
      setPrinting(false);
    }
  };

  const title = [{ text: TITLES.MARKETPLACE_LABELS, route: ROUTES.MARKETPLACE_LABELS }];

  if (!marketplaceId) return null;

  return (
    <div className="marketplace-labels">
      <TitleBar title={title} />
      <div role="group" aria-label="Marketplace" className="marketplace-labels__marketplaces d-flex flex-wrap gap-2 mb-3">
        {MARKETPLACES.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={item.id === marketplace?.id}
            disabled={!item.enabled}
            className={`btn ${item.id === marketplace?.id ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => navigate(item.route)}>
            {item.name}
            {!item.enabled && <span className="ms-1 small">(coming soon)</span>}
          </button>
        ))}
      </div>

      {!marketplace ? (
        <p role="alert">This marketplace is not available. Choose TikTok Shop above.</p>
      ) : (
        <>
          <div className="marketplace-labels__toolbar d-flex flex-wrap align-items-end gap-2 mb-3">
            <label className="marketplace-labels__search d-flex flex-column">
              <span className="small">Search</span>
              <input
                type="search"
                className="form-control"
                value={search}
                placeholder="Name, SKU, color, size or barcode"
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <label className="marketplace-labels__format d-flex flex-column">
              <span className="small">Label size</span>
              <select className="form-select" value={labelFormat} onChange={chooseLabelFormat}>
                {LABEL_FORMATS.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
              </select>
            </label>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!selectedCount || printing}
              onClick={print}>
              {printing && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
              {printing ? 'Preparing labels…' : `Print labels${selectedCount ? ` (${selectedTotal})` : ''}`}
            </button>
          </div>

          {status === 'loading' && <p role="status">Loading TikTok FBT goods…</p>}
          {status === 'error' && (
            <div role="alert" className="d-flex flex-wrap align-items-center gap-2">
              <span>Unable to load TikTok FBT goods.</span>
              <button type="button" className="btn btn-outline-secondary" onClick={load}>Try again</button>
            </div>
          )}
          {status === 'loaded' && !items.length && (
            <p>No TikTok FBT goods are linked to products you supply.</p>
          )}
          {status === 'loaded' && items.length > 0 && !visible.length && <p>No goods match “{search}”.</p>}
          {status === 'loaded' && visible.length > 0 && (
            <div className="table-responsive marketplace-labels__table">
              <table className="table align-middle bg-white">
                <thead>
                  <tr>
                    <th scope="col">
                      <input
                        type="checkbox"
                        aria-label="Select all printable goods"
                        checked={allVisibleSelected}
                        disabled={!printable.length}
                        onChange={toggleAll}
                      />
                    </th>
                    <th scope="col">Seller SKU</th>
                    <th scope="col">Color</th>
                    <th scope="col">Size</th>
                    <th scope="col">Barcode</th>
                    <th scope="col">Labels</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => {
                    const selected = item.goods_id in selection;
                    return (
                      <tr key={item.goods_id}>
                        <td data-label="Select">
                          <input
                            type="checkbox"
                            aria-label={`Select ${item.name}`}
                            checked={selected}
                            disabled={!item.barcode}
                            onChange={() => toggle(item.goods_id)}
                          />
                        </td>
                        <td data-label="Seller SKU" title={item.name}>
                          {skuValues(item, 'seller_sku') || item.skus?.[0]?.sku_id}
                        </td>
                        <td data-label="Color">{skuValues(item, 'color')}</td>
                        <td data-label="Size">{skuValues(item, 'size')}</td>
                        <td data-label="Barcode">
                          {item.barcode ? (
                            <BarcodePreview code={item.barcode.code} symbology={item.barcode.symbology} />
                          ) : (
                            <span className="text-danger">No barcode</span>
                          )}
                        </td>
                        <td data-label="Labels">
                          <input
                            type="number"
                            inputMode="numeric"
                            min={1}
                            max={LIMITS.MAX_QTY}
                            step={1}
                            className="form-control marketplace-labels__qty"
                            aria-label={`Label quantity for ${item.name}`}
                            disabled={!selected}
                            value={selected ? selection[item.goods_id] : ''}
                            onChange={(event) => setQuantity(item.goods_id, event.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
